import { solicitar } from '../identidad/cliente-identidad.js'

export interface DocumentoLegal {
  version: string
  contenido: string
  hashSha256: string
  decision: 'ACEPTADO' | 'RECHAZADO' | null
  decididoEn: string | null
  pendiente: boolean
}

export interface Expediente {
  id: string
  procesoId: string
  estudianteId: string
  estudianteUsuarioId: string
  estudianteUsuarioEstado: 'ACTIVO' | 'INACTIVO'
  estudianteUsuarioVersion: number
  estudianteNombre: string
  programaNombre: string
  periodoCodigo: string
  etapa: 'EVALUACION' | 'EJECUCION'
  estado: 'PENDIENTE' | 'ABIERTO' | 'CERRADO'
  asesorNombre: string
  titularNombre: string
  institucionNombre: string | null
  abiertoEn: string | null
  cerradoEn: string | null
  version: number
}

export interface Identificacion {
  estudianteId: string
  nombres: string
  apellidos: string
  correo: string
  fechaIngreso: string
  programaNombre: string
  periodoCodigo: string
  institucionNombre: string
  sedeNombre: string
  jornadaNombre: string
  fotoArchivoId: string | null
  genero: 'HOMBRE' | 'MUJER' | 'OTRO' | null
  zona: 'Urbana' | 'Rural' | null
  grupoEtnico: string | null
  procedencia: string | null
  trayectosEducativos: string | null
  vulnerabilidadViolenciaMigracion: string | null
  celular: string | null
  arl: string | null
  direccion: string | null
  departamentoCodigo: string | null
  departamentoNombre: string | null
  municipioCodigo: string | null
  municipioNombre: string | null
  departamentoNacimientoCodigo: string | null
  departamentoNacimientoNombre: string | null
  municipioNacimientoCodigo: string | null
  municipioNacimientoNombre: string | null
  departamentoPracticaCodigo: string | null
  departamentoPracticaNombre: string | null
  municipioPracticaCodigo: string | null
  municipioPracticaNombre: string | null
  version: number
  puedeEditar: boolean
  completa: boolean
  autorias: Array<{ campo: string; autorNombre: string; actualizadoEn: string }>
}

export interface Ubicacion {
  codigo: string
  nombre: string
}

export const consultarDocumentoLegal = (): Promise<DocumentoLegal> =>
  solicitar('/api/v1/documentos-legales/vigente')

export const decidirDocumentoLegal = (
  documento: DocumentoLegal,
  resultado: 'ACEPTADO' | 'RECHAZADO',
): Promise<void> =>
  solicitar(`/api/v1/documentos-legales/${encodeURIComponent(documento.version)}/aceptaciones`, {
    method: 'POST',
    body: JSON.stringify({ hashSha256: documento.hashSha256, resultado }),
  })

export const listarExpedientes = (
  consulta: { pagina?: number; limite?: number; busqueda?: string } = {},
): Promise<{ datos: Expediente[]; pagina: number; limite: number; total: number }> => {
  const parametros = new URLSearchParams()
  if (consulta.pagina) parametros.set('pagina', String(consulta.pagina))
  if (consulta.limite) parametros.set('limite', String(consulta.limite))
  if (consulta.busqueda) parametros.set('busqueda', consulta.busqueda)
  return solicitar(`/api/v1/expedientes${parametros.toString() ? `?${parametros.toString()}` : ''}`)
}

export const abrirExpediente = (
  expediente: Pick<Expediente, 'id' | 'version'>,
): Promise<{ id: string }> =>
  solicitar(`/api/v1/expedientes/${expediente.id}/abrir`, {
    method: 'POST',
    body: JSON.stringify({ version: expediente.version }),
  })

export const cerrarExpediente = (expediente: Pick<Expediente, 'id' | 'version'>): Promise<void> =>
  solicitar(`/api/v1/expedientes/${expediente.id}/cerrar`, {
    method: 'POST',
    body: JSON.stringify({ version: expediente.version }),
  })

export const consultarIdentificacion = (estudianteId: string): Promise<Identificacion> =>
  solicitar(`/api/v1/estudiantes/${estudianteId}/identificacion`)

export const actualizarIdentificacion = (
  estudianteId: string,
  datos: {
    genero: string
    zona?: string
    grupoEtnico?: string
    trayectosEducativos: string
    procedencia?: string
    vulnerabilidadViolenciaMigracion?: string
    celular: string
    arl: string
    direccion: string
    departamentoCodigo?: string
    municipioCodigo?: string
    departamentoNacimientoCodigo?: string
    municipioNacimientoCodigo?: string
    departamentoPracticaCodigo?: string
    municipioPracticaCodigo?: string
    version: number
  },
): Promise<Identificacion> =>
  solicitar(`/api/v1/estudiantes/${estudianteId}/identificacion`, {
    method: 'PATCH',
    body: JSON.stringify(datos),
  })

export const cargarFotoIdentificacion = (
  estudianteId: string,
  archivo: File,
  version: number,
): Promise<Identificacion> => {
  const formulario = new FormData()
  formulario.append('archivo', archivo)
  formulario.append('version', String(version))
  return solicitar(`/api/v1/estudiantes/${estudianteId}/identificacion/foto`, {
    method: 'POST',
    body: formulario,
  })
}

export const listarDepartamentos = (): Promise<{ datos: Ubicacion[] }> =>
  solicitar('/api/v1/ubicaciones/departamentos')

export const listarMunicipios = (departamento: string): Promise<{ datos: Ubicacion[] }> =>
  solicitar(`/api/v1/ubicaciones/municipios?departamento=${encodeURIComponent(departamento)}`)
